import type { ReactNode } from 'react';
import type { GuideCategory, GuideWindow, ProgramStore } from '@player/domain';
import { FONT_STACK, colors, layout } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import type { GuideController } from '../guide/GuideController.js';
import { GuideGrid } from '../guide/GuideGrid.js';
import { GuidePreview } from '../guide/GuidePreview.js';

/**
 * La guida TV, nella forma del prototipo.
 *
 * ## Cos'era prima, e perché era sbagliato
 *
 * Era una **sovrapposizione** dentro la diretta: uno strato nero all'88% con la sola griglia sopra.
 * Il prototipo invece ha una schermata con tre parti — la colonna delle categorie a 290 px,
 * l'intestazione con l'anteprima del programma a fuoco, e sotto la griglia — e nessuna delle prime
 * due esisteva. Mancavano quindi **due modi interi di usare la guida**: restringere a una categoria,
 * e leggere di cosa parla il programma prima di premere OK.
 *
 * Non era una mancanza di rifinitura. Su un catalogo vero — dodicimila canali — una guida senza
 * categorie è una lista in cui si scorre a caso, e il fatto che le categorie ci fossero nei dati
 * (`Channel.categoryId`) senza che nessuno le mostrasse è la forma esatta di «dichiarato e mai in
 * vigore».
 *
 * ## Questa schermata non possiede niente
 *
 * Né il cursore delle categorie, né quale area ha il fuoco: li possiede chi ha **l'iscrizione al
 * telecomando**, cioè la diretta. Tenerli qui vorrebbe dire una seconda iscrizione, e due iscrizioni
 * attive insieme ricevono la stessa freccia: il cursore della griglia si muoverebbe *e* si
 * muoverebbe la voce della categoria. È un difetto che si vede solo su un apparecchio vero, dove è
 * anche difficile da raccontare.
 */

export interface GuideScreenProps {
  readonly controller: GuideController;
  readonly programs: ProgramStore;
  readonly window: GuideWindow;
  readonly serverNowMs: number;
  /** Le categorie già contate: le costruisce `buildGuideCategories`, non questa schermata. */
  readonly categories: ReadonlyArray<GuideCategory>;
  /** Quale categoria filtra la griglia. */
  readonly categoryId: string;
  /** Dove sta il cursore nella colonna, anche quando il fuoco è della griglia. */
  readonly categoryCursor: number;
  readonly onChooseCategory: (id: string, index: number) => void;
  readonly favoriteIds: ReadonlySet<string>;
  /** I promemoria, con la chiave di `reminderKey`: canale più istante. */
  readonly reminderIds: ReadonlySet<string>;
  /** Porta alla schermata dei preferiti: è il tasto della categoria vuota, nel disegno. */
  readonly onOpenFavorites?: () => void;
  /**
   * Dove sono i tasti: la colonna delle categorie, la card del canale, o le celle dei programmi.
   *
   * Qui serve solo a disegnare il fuoco — chi lo possiede è la diretta, che ha l'iscrizione al
   * telecomando. Tre valori perché nel disegno la card del canale è un bersaglio a sé (vedi
   * `GuideGridProps.channelFocused`).
   */
  readonly focusArea: 'categories' | 'channel' | 'grid';
  /** La fascia del motivo, quando l'elenco si è aperto perché un canale non partiva. */
  readonly notice?: ReactNode;
  /** Un clic nella griglia: lo smista chi possiede la scelta, non questa schermata. */
  readonly onPick?: (channelIndex: number, slot: number, confirmed: boolean) => void;
  /** Inverte il preferito dalla stella sulla riga del canale. */
  readonly onToggleFavorite?: (channelId: string) => void;
  /**
   * Quanto spazio lasciare a destra dell'intestazione per l'anteprima del video.
   *
   * Lo decide chi disegna il video — questa schermata non sa nemmeno che ci sia — ma deve saperlo in
   * pixel, o la trama del programma finirebbe sotto al riquadro e si leggerebbe a metà.
   */
  readonly previewReservePx?: number;
  /**
   * La riga che dice cosa fa Indietro: «torna a Rai 1».
   *
   * Entrare nella guida per sbaglio deve costare una pressione, e la pressione va **detta**: su un
   * telecomando non c'è niente che suggerisca che Indietro riporti al canale di prima invece di
   * uscire.
   */
  readonly backHint?: string;
}

/**
 * Quante voci restano appiccicate in cima alla colonna: «Tutti» e «Preferiti».
 *
 * Sono le due che `buildGuideCategories` mette davanti a quelle del fornitore, ed è il motivo per cui
 * il numero è due e non configurabile: se quell'elenco cambiasse, cambierebbero insieme.
 */
const PINNED_CATEGORIES = 2;

/**
 * Il fondo di una voce della colonna delle categorie.
 *
 * ## Perché è una funzione e non tre righe dentro lo stile
 *
 * Perché ci sono già state **due** `background` nello stesso oggetto di stile, una dentro il ramo
 * delle appiccicate e una sotto: vinceva la seconda, `transparent`, e le categorie che scorrevano
 * si leggevano attraverso «Tutti» e «Preferiti». Sullo schermo erano due testi sovrapposti; nel
 * codice non era un conflitto per nessuno — una proprietà ripetuta vince l'ultima, in silenzio.
 *
 * Qui la regola è una sola espressione, e si prova senza disegnare la guida.
 */
export function categoryBackground(active: boolean, pinned: boolean): string {
  if (active) return colors.surfaceRaised;
  // Le appiccicate hanno un fondo **pieno** perché le altre scorrono sotto di loro. Le altre no:
  // un fondo su tutte trasformerebbe la colonna in un blocco grigio invece di un elenco.
  return pinned ? colors.background : 'transparent';
}

export function GuideScreen({
  controller,
  programs,
  window: guideWindow,
  serverNowMs,
  categories,
  categoryId,
  categoryCursor,
  onChooseCategory,
  favoriteIds,
  reminderIds,
  focusArea,
  onOpenFavorites,
  notice,
  onPick,
  onToggleFavorite,
  previewReservePx = 0,
  backHint,
}: GuideScreenProps): ReactNode {
  const translator = useTranslator();

  return (
    <div data-guide="1" style={{ flex: 1, minWidth: 0, display: 'flex' }}>
      {/* La colonna delle categorie: 290 px, respiro 32 a sinistra e 20 a destra. */}
      <div
        data-guide-categories="1"
        style={{
          flex: 'none',
          width: layout.guideCategoryWidth,
          padding: '56px 20px 40px 32px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          borderRight: `1px solid ${colors.borderFaint}`,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          boxSizing: 'border-box',
          // Smorzata quando i tasti sono della griglia: dice dove sono le frecce senza scrivere una
          // parola, ed è la stessa convenzione del menu.
          opacity: focusArea === 'categories' ? 1 : 0.72,
        }}
      >
        <div style={{ font: `700 44px ${FONT_STACK}`, padding: '0 18px 8px' }}>
          {translator.t('guide.title')}
        </div>
        {/*
          L'unica riga che resta: dove riporta Indietro.

          Non è una spiegazione di come funziona la guida — è un'informazione che cambia, e che su un
          telecomando non si può dedurre: niente suggerisce che Indietro riporti al canale di prima
          invece di uscire.
        */}
        {backHint !== undefined && (
          <div
            data-guide-back-hint="1"
            style={{ font: `500 18px ${FONT_STACK}`, color: colors.accent, padding: '0 18px 22px' }}
          >
            {backHint}
          </div>
        )}
        {/*
          **Niente righe di spiegazione.**

          Qui c'erano «Le categorie arrivano dal gateway» e «★ preferito · OK sul logo per aggiungere
          o togliere». La prima è un dettaglio di come è fatta la piattaforma, e a chi guarda la
          televisione non dice niente. La seconda spiegava un gesto invisibile — e un gesto che va
          spiegato è un gesto mal disegnato: al suo posto c'è adesso una stella premibile su ogni
          riga, che non ha bisogno di istruzioni.

          Se qualcosa richiede una didascalia per essere usato, la didascalia non è la correzione.
        */}
        {categories.map((entry, index) => {
          const focused = focusArea === 'categories' && index === categoryCursor;
          const active = entry.id === categoryId;
          /*
            **Le due voci dell'app restano in cima.**

            Su un'installazione vera le categorie sono ottanta, con nomi lunghi che occupano due righe:
            la colonna scorre, e «Tutti» — la voce che riporta a vedere l'intero catalogo — sparisce
            dopo la seconda schermata. Per tornarci bisogna risalire ottanta voci, e nel frattempo non
            c'è niente che dica che esiste ancora.

            Sono appiccicate al bordo perché sono **le uniche due che non vengono dal fornitore**: non
            hanno un posto nell'alfabeto, e non devono averlo.
          */
          const pinned = index < PINNED_CATEGORIES;
          return (
            <button
              key={entry.id}
              type="button"
              data-f="1"
              data-category={entry.id}
              data-focused={index === categoryCursor ? '1' : '0'}
              data-active={active ? '1' : '0'}
              onClick={() => onChooseCategory(entry.id, index)}
              style={{
                /*
                  Alta **almeno** 60, non esattamente 60.

                  I nomi veri di un fornitore — «Documentari e cultura», «Sport — Calcio estero» —
                  non stanno su una riga nemmeno con la colonna allargata. Con un'altezza fissa la
                  seconda riga sarebbe tagliata a metà: peggio dei puntini, perché sembra un difetto
                  di disegno invece di un testo abbreviato.
                */
                flex: 'none',
                minHeight: 60,
                padding: '8px 18px',
                ...(pinned
                  ? {
                      position: 'sticky' as const,
                      // Una sotto l'altra: la seconda si ferma sotto la prima, non sopra di essa.
                      top: index * 68,
                      zIndex: 2 - index,
                    }
                  : {}),
                border: 0,
                margin: 0,
                borderRadius: 12,
                textAlign: 'left',
                cursor: 'pointer',
                // Attiva e a fuoco restano due cose diverse: «cosa sto guardando» e «cosa sto per
                // premere». Una colonna che le confonde risponde alla domanda sbagliata. Il fondo
                // delle appiccicate è l'altra metà: vedi `categoryBackground`.
                background: categoryBackground(active, pinned),
                /*
                  Non attiva è **grigio chiaro**, non bianco.

                  Il prototipo scrive `#C9D0DB` (`textMuted`); io avevo messo `#F2F4F7`, il bianco del
                  testo normale. Su una colonna di otto voci la differenza non è estetica: col bianco
                  tutte le categorie pesano quanto quella scelta, e l'unica in ambra non spicca più —
                  cioè si perde l'informazione «dove sono».
                */
                color: active ? colors.accent : colors.textMuted,
                font: `600 23px ${FONT_STACK}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                outline: focused ? `4px solid ${colors.accent}` : 'none',
                outlineOffset: 4,
                transform: focused ? 'scale(var(--fs, 1.04))' : 'none',
                transition: 'var(--tr, transform .16s ease)',
              }}
            >
              <span
                style={{
                  /*
                    Due righe, poi i puntini.

                    `-webkit-line-clamp` fa esattamente quel che serve: va a capo finché ci sta, e
                    abbrevia solo quando due righe non bastano. Con `nowrap` si abbreviava **sempre**,
                    anche quando lo spazio per andare a capo c'era.
                  */
                  minWidth: 0,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.15,
                  wordBreak: 'break-word',
                }}
              >
                {/* Il nome del fornitore non si traduce; le due voci dell'app sì. */}
                {entry.translated ? translator.t(entry.label) : entry.label}
              </span>
              <span style={{ font: `500 19px ${FONT_STACK}`, color: colors.textFaint, flex: 'none' }}>
                {entry.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* L'area della griglia: intestazione, riga del tempo, righe. */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: '52px 48px 0 32px',
          boxSizing: 'border-box',
        }}
      >
        {notice}

        <div
          style={{
            flex: 'none',
            display: 'flex',
            gap: 36,
            alignItems: 'flex-start',
            // Lo spazio a destra è dell'anteprima del video: senza, la trama del programma ci
            // finirebbe sotto e si leggerebbe a metà.
            paddingRight: previewReservePx,
          }}
        >
          <GuidePreview
            controller={controller}
            programs={programs}
            serverNowMs={serverNowMs}
            reminderIds={reminderIds}
          />
          {/*
            Niente orologio, e niente altro in quest'angolo: **ci sta l'anteprima del video**.

            L'orologio era nel prototipo e non serve a nessuno qui — l'ora ce l'ha il televisore — e
            quello spazio è l'unico dove un riquadro video non copre né le categorie né le righe.
          */}
        </div>

        <GuideGrid
          controller={controller}
          programs={programs}
          window={guideWindow}
          serverNowMs={serverNowMs}
          favoriteIds={favoriteIds}
          reminderIds={reminderIds}
          channelFocused={focusArea === 'channel'}
          onPick={onPick}
          onToggleFavorite={onToggleFavorite}
          onOpenFavorites={onOpenFavorites}
        />
      </div>
    </div>
  );
}
